import { formatDuration, type PlaybackState } from '../lib/api';
import { youtubeThumbnailUrl } from '../lib/youtube';
import RemovalFlag from './RemovalFlag';
import './NowPlaying.css';

function phaseLabel(playback: PlaybackState): string {
  switch (playback.phase) {
    case 'preparing':
      return 'Preparing';
    case 'seeking':
      return 'Seeking';
    case 'countdown':
      return 'Starting';
    case 'playing':
      return 'Playing';
    case 'paused':
      return 'Paused';
    case 'failed':
      return 'Unavailable';
    case 'break':
      return 'Intermission';
    default:
      return playback.is_playing ? 'Playing' : 'Paused';
  }
}

export default function NowPlaying({
  playback,
  theaterCount,
  isAdmin,
  onPause,
  onResume,
  onRestart,
  onSkip,
  onStop,
  onSeek,
  onStartAnyway,
  username,
  onFlag,
  watchOnly = false,
}: {
  playback: PlaybackState | null;
  theaterCount: number;
  isAdmin: boolean;
  onPause: () => void;
  onResume: () => void;
  onRestart: () => void;
  onSkip: () => void;
  onStop: () => void;
  onSeek: (seconds: number) => void;
  onStartAnyway?: () => void;
  username: string;
  onFlag?: (id: string, flagged: boolean) => void;
  watchOnly?: boolean;
}) {
  if (!playback?.movie) {
    const isBreak = playback?.phase === 'break';
    return (
      <section className="now-playing panel fade-up">
        <h2 className="section-title">{isBreak ? (playback.next_movie ? 'UP NEXT' : 'VIDEO ENDED') : 'NOW PLAYING'}</h2>
        <p className="muted">
          {isBreak
            ? playback.next_movie
              ? `${playback.next_movie.title} is up next.`
              : 'That YouTube video has ended.'
            : watchOnly
              ? 'The theater is quiet right now.'
              : 'The theater is quiet. Request a YouTube video to get the night started.'}
        </p>
        {isAdmin && isBreak && (
          <div className="admin-controls" style={{ marginTop: '0.85rem' }}>
            <button className="btn btn-gold" onClick={onSkip}>
              Skip Break / Next
            </button>
            <button className="btn" onClick={onStop}>
              End Night
            </button>
          </div>
        )}
      </section>
    );
  }

  const duration = playback.duration_seconds ?? 0;
  const pct = duration > 0 ? Math.min(100, (playback.position_seconds / duration) * 100) : 0;
  const thumb = youtubeThumbnailUrl(playback.movie.youtube_video_id);
  const preparing = playback.phase === 'preparing' || playback.phase === 'seeking';
  const canSeek = isAdmin && duration > 0 && (playback.phase === 'playing' || playback.phase === 'paused');
  const flaggedBy = playback.flagged_by || [];
  const queueItemId = playback.queue_item_id;

  return (
    <section className="now-playing panel fade-up">
      <h2 className="section-title">NOW PLAYING</h2>
      <div className="now-playing-grid now-playing-text">
        {thumb && (
          <img className="now-thumb" src={thumb} alt="" width={160} height={90} />
        )}
        <div className="now-meta">
          <h3 className="now-title">
            {playback.movie.title}
            {queueItemId && !watchOnly && (
              <RemovalFlag
                isAdmin={isAdmin}
                flagCount={playback.flag_count || 0}
                flaggedBy={flaggedBy}
                flaggedByMe={flaggedBy.includes(username)}
                onToggle={() => onFlag?.(queueItemId, !flaggedBy.includes(username))}
              />
            )}
          </h3>
          <p className="muted">
            {[
              playback.movie.duration_seconds
                ? formatDuration(playback.movie.duration_seconds)
                : null,
              phaseLabel(playback),
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
          {playback.phase === 'failed' && (
            <p className="player-status player-error" style={{ marginTop: '0.75rem' }}>
              {playback.error || 'This YouTube video cannot be played in the Zemew theater.'}{' '}
              Skipping to the next video…
            </p>
          )}
          {playback.readiness && preparing && (
            <p className="viewers">
              {playback.readiness.ready} / {playback.readiness.required} ready
            </p>
          )}
          <p className="runtime">
            {formatDuration(playback.position_seconds)}
            {duration > 0 ? ` / ${formatDuration(duration)}` : ''}
          </p>
          {duration > 0 && playback.phase !== 'failed' && (
            <div className="progress-track">
              <div className="progress-fill" style={{ width: `${pct}%` }} />
            </div>
          )}
          {canSeek && (
            <input
              className="seek-range"
              type="range"
              min={0}
              max={Math.max(1, duration)}
              value={Math.min(playback.position_seconds, duration || playback.position_seconds)}
              onChange={(e) => onSeek(Number(e.target.value))}
            />
          )}
          <p className="viewers">
            🎬 {theaterCount} {theaterCount === 1 ? 'person' : 'people'} in the theater
          </p>
          {isAdmin && (
            <div className="admin-controls">
              {preparing && (
                <button className="btn btn-gold" onClick={() => onStartAnyway?.()}>
                  Start Anyway
                </button>
              )}
              {playback.phase === 'failed' ? (
                <>
                  <button className="btn" onClick={onStop}>
                    Remove Request
                  </button>
                  <button className="btn btn-gold" onClick={onSkip}>
                    Skip to Next
                  </button>
                </>
              ) : (
                <>
                  {playback.phase === 'playing' ? (
                    <button className="btn btn-gold" onClick={onPause}>
                      Pause
                    </button>
                  ) : playback.phase === 'paused' || playback.phase === 'countdown' ? (
                    <button className="btn btn-gold" onClick={onResume}>
                      Play
                    </button>
                  ) : null}
                  <button className="btn" onClick={onRestart}>
                    Restart
                  </button>
                  <button className="btn" onClick={onSkip}>
                    Skip / Next
                  </button>
                  <button className="btn" onClick={onStop}>
                    Stop
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
